<script lang="ts">
  import { createEventDispatcher, onMount } from "svelte";
  import TipTapEditor from "$lib/admin/components/content/TipTapEditor/TipTapEditor.svelte";
  import { Button, Input, Label, Card, Modal } from "flowbite-svelte";
  import Icon from "@iconify/svelte";
  import OperatorSelector from "$lib/admin/components/operators/OperatorSelector/index.svelte";
  import SortableContainer from "$lib/admin/components/common/SortableContainer.svelte";

  export let props: any;

  interface ListItem {
    operatorId: string;
    title: string;
    content: string;
  }

  const dispatch = createEventDispatcher();
  let deleteModalOpen = false;
  let itemToDelete: number | null = null;

  function addListItem() {
    props.listItems = [
      ...props.listItems,
      {
        id: `item-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        operatorId: "",
        title: "",
        content: "",
      },
    ];
  }

  function confirmDeleteItem(index: number) {
    itemToDelete = index;
    deleteModalOpen = true;
  }

  function deleteItem() {
    if (itemToDelete !== null && props.listItems.length > 0) {
      props.listItems = props.listItems.filter((_: ListItem, index: number) => index !== itemToDelete);
      dispatch("keyup");
    }
    deleteModalOpen = false;
    itemToDelete = null;
  }

  function cancelDelete() {
    deleteModalOpen = false;
    itemToDelete = null;
  }

  function handleSortableUpdate(event: CustomEvent) {
    const { items } = event.detail;
    props.listItems = items;
  }

  $: {
    if (props.listItems === undefined) {
      props.listItems = [];
    }
  }
</script>

<p class="mb-1 text-sm text-gray-500">
  <strong>Attention:</strong> To add a background image into an operator, you need to upload it first in the operators section.
</p>
<p class="mb-4 text-sm text-gray-500">You can add up to 4 items. Use the drag handles to re-order them.</p>
<hr class="my-4" />
<div class="mb-4">
  {#if props.listItems?.length < 4}
    <Button color="blue" on:click={() => addListItem()}>
      <Icon icon="heroicons:plus" class="mr-2 w-5 h-5" />
      Add highlighted casino
    </Button>
  {/if}
</div>
<SortableContainer
  items={props.listItems}
  handleClass="drag-handle"
  on:update={handleSortableUpdate}
  on:keyup={() => dispatch("keyup")}
  generateId={true}
  idField="id"
  autoNotify={true}
  notifyDelay={0}
  containerClass="grid gap-3 relative highlighted-casino-container
                  {props.listItems?.length === 1 || props.listItems?.length === 2 ? 'md:grid-cols-2' : ''}
                  {props.listItems?.length === 3 || props.listItems?.length === 4 ? 'md:grid-cols-3' : ''}
                  {props.listItems?.length === 4 ? 'md:grid-cols-4' : ''}">
  {#if props.listItems?.length > 0}
    {#each props.listItems as item, index (item.id)}
      <Card class="relative p-4 max-w-sm border border-gray-200 card-wrapper" data-id={item.id}>
        <div class="flex justify-between items-center mb-2">
          <div class="text-sm font-medium text-gray-500">Item {index + 1}</div>
          <div class="flex gap-2 items-center">
            <button
              class="text-red-500 transition-colors hover:text-red-700"
              on:click={() => confirmDeleteItem(index)}
              title="Delete item">
              <Icon icon="mdi:delete" class="w-5 h-5" />
            </button>
            <div class="text-gray-400 cursor-grab drag-handle">
              <Icon icon="mdi:arrow-all" class="w-5 h-5" />
            </div>
          </div>
        </div>

        <div class="space-y-4">
          <div>
            <OperatorSelector
              operatorId={item.operatorId ? parseInt(item.operatorId) : null}
              on:select={(e) => {
                item.operatorId = e.detail;
                dispatch("keyup");
              }}
              label={`Operator ${index + 1}`} />
          </div>
          <div>
            <Label for="title-{index}" class="mb-2">Title</Label>
            <Input id="title-{index}" type="text" required bind:value={item.title} on:keyup />
          </div>
          <div>
            <Label class="mb-2">Content</Label>
            <TipTapEditor bind:text={item.content} on:keyup compactMode={true} />
          </div>
        </div>
      </Card>
    {/each}
  {/if}
</SortableContainer>

<Modal bind:open={deleteModalOpen} size="xs" autoclose>
  <div class="text-center">
    <Icon icon="mdi:delete" class="mx-auto mb-4 w-12 h-12 text-gray-400 dark:text-gray-200" />
    <h3 class="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
      Are you sure you want to delete this item?
    </h3>
    <Button color="red" class="me-2" on:click={deleteItem}>Yes, delete it</Button>
    <Button color="alternative" on:click={cancelDelete}>No, cancel</Button>
  </div>
</Modal>

<style>
  :global(.ProseMirror) {
    min-height: 100px;
    padding: 0.5rem;
    border-radius: 0.375rem;
  }

  :global(.highlighted-casino-container .just-dropped > div:after) {
    content: "✓";
    position: absolute;
    top: 24px;
    right: 24px;
    background-color: #046c4e;
    color: white;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    opacity: 1;
    animation: checkmark-fade 1s ease forwards;
  }

  @keyframes checkmark-fade {
    0% {
      opacity: 0;
      transform: scale(0);
    }
    20% {
      opacity: 1;
      transform: scale(1.2);
    }
    40% {
      transform: scale(1);
    }
    80% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
</style>
